import { useId, useState } from 'react'
import { valoresDaLinha } from '@domain/receipt'
import type { ItemEdit, ReceiptItem } from '@domain/receipt'
import type { ReceiptItemPatch } from '@domain/ports/receipt-repository'
import { formatBRL } from '@shared/money/format'
import { centsToInput, parseAmountInput } from '@shared/money/parse'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/**
 * Corrigir o que a leitura errou numa linha: nome, quantidade, valores e
 * desconto.
 *
 * ## O lido fica ao lado, e a folha o mostra
 *
 * A correção é gravada em `edited`, e nunca por cima do cru (ver
 * `ReceiptItem.edited`). A folha diz o que foi lido logo no topo: quem corrige
 * está olhando o papel, e comparar os dois é o gesto inteiro.
 *
 * ## Mexer na quantidade ou no unitário refaz o total
 *
 * É a conta que o cupom imprime, e é o erro mais comum da leitura — o preço do
 * vizinho, ou "1 ×" onde eram seis. Refazer o total poupa uma conta de cabeça.
 * O contrário não acontece: mexer no total não mexe no unitário, porque a
 * balança arredonda, e dividir de volta inventaria um preço que o cupom não
 * imprimiu. Se os três não fecharem, a linha avisa, como avisa para o lido.
 *
 * ## Salvar igual ao lido grava "sem correção"
 *
 * E não uma correção idêntica ao lido, que marcaria a linha como "editada"
 * sem ninguém ter mudado nada.
 */
export function EditarLinha({
  linha,
  nomeSemCorrecao,
  onSalvar,
  onFechar,
}: {
  linha: ReceiptItem
  /** O nome que a linha mostra sem correção: o do produto, ou o lido. */
  nomeSemCorrecao: string
  onSalvar: (patch: ReceiptItemPatch) => void
  onFechar: () => void
}) {
  const id = useId()
  const valores = valoresDaLinha(linha)
  const [nome, setNome] = useState(linha.edited?.name ?? '')
  const [quantidade, setQuantidade] = useState(quantidadeParaCampo(valores.quantity))
  const [unitario, setUnitario] = useState(centsToInput(valores.unitPriceCents))
  const [total, setTotal] = useState(centsToInput(valores.totalCents))
  const [desconto, setDesconto] = useState(centsToInput(linha.discountCents))

  const q = lerQuantidade(quantidade)
  const u = lerValor(unitario)
  const t = lerValor(total)
  const d = desconto.trim() === '' ? 0 : lerValor(desconto)

  const refazerTotal = (novaQuantidade: string, novoUnitario: string) => {
    const nq = lerQuantidade(novaQuantidade)
    const nu = lerValor(novoUnitario)
    if (nq !== null && nu !== null) setTotal(centsToInput(Math.round(nq * nu)))
  }

  const erro =
    q === null
      ? 'A quantidade precisa ser um número maior que zero.'
      : u === null || t === null || d === null
        ? 'Algum valor não é um valor em reais.'
        : d > t
          ? 'O desconto não pode passar do total da linha.'
          : null

  const salvar = () => {
    if (q === null || u === null || t === null || d === null || d > t) return
    const igualAoLido =
      nome.trim() === '' &&
      q === linha.quantity &&
      u === linha.unitPriceCents &&
      t === linha.totalCents
    const edited: ItemEdit | null = igualAoLido
      ? null
      : { name: nome.trim() === '' ? null : nome.trim(), quantity: q, unitPriceCents: u, totalCents: t }
    onSalvar({ edited, discountCents: d })
    onFechar()
  }

  const unidade = linha.unit?.trim().toLowerCase() ?? ''
  // `min-h-11`: 44px, o alvo de toque do sistema — os campos mediam 40,85px.
  const campo =
    'min-h-11 w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm tabular-nums'

  return (
    <FolhaDeBaixo titulo={`Editar ${linha.rawText}`} onFechar={onFechar}>
      <form
        className="flex flex-col gap-3 p-1"
        onSubmit={(e) => {
          e.preventDefault()
          salvar()
        }}
      >
        <div>
          <h2 className="text-sm font-medium">Corrigir a linha</h2>
          <p className="mt-0.5 font-mono text-xs text-[var(--text-muted)]">{linha.rawText}</p>
          <p className="mt-0.5 text-xs text-[var(--text-muted)] tabular-nums">
            {`Lido: ${quantidadeParaCampo(linha.quantity)} × ${formatBRL(linha.unitPriceCents)} = ${formatBRL(linha.totalCents)}`}
          </p>
        </div>

        <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-nome`}>
          Nome
          <input
            id={`${id}-nome`}
            value={nome}
            placeholder={nomeSemCorrecao}
            onChange={(e) => {
              setNome(e.target.value)
            }}
            className={campo}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-qtd`}>
            {['kg', 'g', 'l', 'ml'].includes(unidade) ? `Quantidade (${unidade})` : 'Quantidade'}
            <input
              id={`${id}-qtd`}
              inputMode="decimal"
              value={quantidade}
              onChange={(e) => {
                setQuantidade(e.target.value)
                refazerTotal(e.target.value, unitario)
              }}
              className={campo}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-unit`}>
            Preço unitário
            <input
              id={`${id}-unit`}
              inputMode="decimal"
              value={unitario}
              onChange={(e) => {
                setUnitario(e.target.value)
                refazerTotal(quantidade, e.target.value)
              }}
              className={campo}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-total`}>
            Total da linha
            <input
              id={`${id}-total`}
              inputMode="decimal"
              value={total}
              onChange={(e) => {
                setTotal(e.target.value)
              }}
              className={campo}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--text-muted)]" htmlFor={`${id}-desc`}>
            Desconto
            <input
              id={`${id}-desc`}
              inputMode="decimal"
              value={desconto}
              onChange={(e) => {
                setDesconto(e.target.value)
              }}
              className={campo}
            />
          </label>
        </div>

        {erro !== null ? (
          <p className="text-xs" style={{ color: 'var(--pendente)' }}>
            {erro}
          </p>
        ) : (
          t !== null &&
          d !== null && (
            <p className="text-xs text-[var(--text-muted)] tabular-nums">
              {`A linha passa a valer ${formatBRL(t - d)}.`}
            </p>
          )
        )}

        <div className="flex flex-wrap gap-2">
          <Botao tom="primario" type="submit" disabled={erro !== null}>
            Salvar
          </Botao>
          {linha.edited !== null && (
            // Só a correção volta; o desconto é outro campo, e fica como está.
            <Botao
              type="button"
              onClick={() => {
                onSalvar({ edited: null })
                onFechar()
              }}
            >
              Voltar ao que foi lido
            </Botao>
          )}
          <button
            type="button"
            onClick={onFechar}
            className="alvo px-3 text-sm text-[var(--text-muted)]"
          >
            Cancelar
          </button>
        </div>
      </form>
    </FolhaDeBaixo>
  )
}

/** `0.426` → "0,426"; `2` → "2". Como se digita, e não como o JS escreve. */
function quantidadeParaCampo(quantidade: number): string {
  return String(quantidade).replace('.', ',')
}

/**
 * A quantidade digitada, ou `null`. Vírgula ou ponto como decimal, até três
 * casas — é o que a balança imprime —, e maior que zero.
 */
function lerQuantidade(texto: string): number | null {
  const limpo = texto.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,3})?$/.test(limpo)) return null
  const n = Number(limpo)
  return n > 0 ? n : null
}

/** Um valor em reais não negativo, em centavos, ou `null`. */
function lerValor(texto: string): number | null {
  const lido = parseAmountInput(texto)
  if (lido.status !== 'ok' || lido.cents < 0) return null
  return lido.cents
}
